import { FC, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { InstanceContext, IInstanceWithRole } from "../../context/InstanceContext";
import { launchInstancePortal, clearSession } from "../../context/AuthContext";
import { fetchBoxManageQRCode, buildQrCodeString, fetchQRCodeStatus } from "../../services/qrcode";
import { getSwitchAccountUrl } from "../../services/auth";
import { LS_KEY } from "../../constants";
import { getLiteUrl } from "../../utils";
import QrCode from "../QrCode";
import styles from "./index.module.css";

/* sort order aligned with firewalla.web entrance:
   priority role > plan > domain.
   role by [owner, member], plan by [business, personal], domain alphabetical asc. */
const ROLE_ORDER: IInstanceWithRole["role"][] = ["owner", "member"];
const PLAN_ORDER: IInstanceWithRole["plan"][] = ["business", "personal"];

function sortInstances(list: IInstanceWithRole[]): IInstanceWithRole[] {
  return [...list].sort((a, b) => {
    if (a.role !== b.role) {
      return ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role);
    }
    if (a.plan !== b.plan) {
      return PLAN_ORDER.indexOf(a.plan) - PLAN_ORDER.indexOf(b.plan);
    }
    return a.domain < b.domain ? -1 : a.domain > b.domain ? 1 : 0;
  });
}

const PLAN_TAG: Record<IInstanceWithRole["plan"], { label: string; className: string }> = {
  personal: { label: "PRO", className: styles["tag-pro"] },
  business: { label: "BUSINESS", className: styles["tag-business"] },
};

const QRSection: FC<{ value: string; loading: boolean }> = ({ value, loading }) => (
  <div className={`${styles["qr-section"]} ${styles["option-row-bordered"]}`}>
    <div className={styles["qr-text"]}>
      <p className={styles["row-title"]}>MSP Lite</p>
      <p style={{ fontSize: 16, color: '#555', fontWeight: 700 }}>Free web interface for your Firewalla box</p>
      <p className={styles["row-desc"]}>
        Open your Firewalla app and scan this code to log in to{" "}
        <a className={styles.link} href={getLiteUrl()} target="_blank" rel="noreferrer">
          {new URL(getLiteUrl()).host}
        </a>
      </p>
    </div>
    <QrCode
      value={value}
      loading={loading}
      size={148}
      padding={3}
      style={{ borderRadius: 8, border: "1px solid #e8ebf1" }}
    />
  </div>
);

const CrystalRow: FC = () => {
  const navigate = useNavigate();
  return (
    <div className={`${styles["option-row"]} ${styles["option-row-bordered"]}`}>
      <div className={styles["left-content"]}>
        <img className={styles["icon-lg"]} src="/images/crystal.svg" alt="Firewalla Crystal" />
        <div className={styles["text-stack"]}>
          <p className={styles["row-title"]}>Firewalla Crystal</p>
          <p className={styles["row-desc"]}>Zero Trust Cyber Security on any hardware</p>
        </div>
      </div>
      <button
        type="button"
        className={styles["btn-primary"]}
        onClick={() => navigate("/crystal")}
      >
        Get Firewalla Crystal
      </button>
    </div>
  );
};

const ManageFirewallaCard: FC = () => {
  const navigate = useNavigate();
  const { instancesWithRole } = useContext(InstanceContext);

  const hasMsp = instancesWithRole.length > 0;
  const sortedInstances = hasMsp ? sortInstances(instancesWithRole) : [];

  const [qrValue, setQrValue] = useState("");
  const [qrToken, setQrToken] = useState("");
  const [qrLoading, setQrLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setQrLoading(true);
        const info = await fetchBoxManageQRCode();
        if (mounted) {
          setQrValue(buildQrCodeString(info));
          setQrToken(info.token);
        }
      } catch (err) {
        console.error("Failed to load QR code", err);
        message.error("Failed to load QR code.");
      } finally {
        if (mounted) setQrLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // poll the QR login status; on scan (scanned/box_added) redirect to the Lite Portal.
  // mirrors firewalla.web entrance's 5s polling fallback (we have no websocket here).
  // pause while the tab is hidden and resume on return, so we don't poll off-screen.
  useEffect(() => {
    if (!qrToken) return;
    let isMounted = true;
    let isRequesting = false;
    let timer: ReturnType<typeof setInterval> | undefined;

    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = undefined;
      }
    };

    const checkStatus = async () => {
      if (isRequesting) return;
      isRequesting = true;
      try {
        const res = await fetchQRCodeStatus(qrToken);
        if (!isMounted) return;
        // `scanned` only means the app read the code; wait for `box_added`
        // (user confirmed "add box" in the app) before redirecting.
        if (res?.status === "box_added") {
          stop();
          // push (not replace) so the back button returns here to the QR page.
          // the pageshow/bfcache handler below reloads a fresh QR on return so we
          // don't immediately re-redirect with the stale, already-scanned token.
          window.location.href = getLiteUrl();
        }
      } catch (err) {
        console.error("Failed to get QR code status", err);
      } finally {
        isRequesting = false;
      }
    };

    const start = () => {
      if (timer) return;
      timer = setInterval(checkStatus, 5000);
    };

    // only poll while the page is visible; pause when the user leaves the tab
    const handleVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    if (!document.hidden) start();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      isMounted = false;
      stop();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [qrToken]);

  // when the user comes back via the browser back button, the page is restored
  // from bfcache with the stale, already-scanned QR (which would immediately
  // re-redirect). reload to fetch a fresh, unscanned QR instead.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) window.location.reload();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  return (
    <div className={styles.card}>
      <div className={styles.header}>Manage Your Firewalla</div>

      {hasMsp && (
        <div className={styles["option-row-bordered"]}>
          <div className={styles["portal-hint"]}>Select a portal to continue</div>
          {sortedInstances.map((instance) => {
            const tag = PLAN_TAG[instance.plan];
            const isOwner = instance.role === "owner";
            return (
              <div
                key={instance.id}
                className={`${styles["option-row"]} ${styles["portal-row"]}`}
                onClick={() => launchInstancePortal(instance, "_blank")}
              >
                <div className={styles["portal-left"]}>
                  <span className={styles["row-title"]}>{instance.name || instance.domain}</span>
                  {tag && <span className={`${styles.tag} ${tag.className}`}>{tag.label}</span>}
                  <span className={`${styles.role} ${isOwner ? styles["role-owner"] : ""}`}>
                    {isOwner ? "Owner" : "Member"}
                  </span>
                </div>
                <img className={styles["icon-sm"]} src="/images/chevron-right.svg" alt="" />
              </div>
            );
          })}
        </div>
      )}

      <QRSection value={qrValue} loading={qrLoading} />

      <CrystalRow />

      {!hasMsp && (
        <div className={`${styles["option-row"]} ${styles["option-row-bordered"]}`}>
          <div className={styles["left-content"]}>
            <img className={styles["icon-lg"]} src="/images/home.svg" alt="MSP Plan" />
            <div className={styles["text-stack"]}>
              <p className={styles["row-title"]}>MSP Plan</p>
              <p className={styles["row-desc"]}>Unlock more features and fully manage your network</p>
            </div>
          </div>
          <button type="button" className={styles["btn-secondary"]} onClick={() => navigate("/plans")}>
            Choose Your MSP Plan
          </button>
        </div>
      )}

      <div className={styles.footer}>
        <span>Not seeing your MSP Portal? </span>
        <span
          className={styles["footer-action"]}
          onClick={() => {
            clearSession();
            window.location.replace(getSwitchAccountUrl());
          }}
        >
          Try a different account →
        </span>
      </div>
    </div>
  );
};

export default ManageFirewallaCard;
